import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import axios from 'axios';
import {
  DndContext, closestCenter, PointerSensor,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext,
  verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Import Quill CSS at top level to prevent flash of unstyled content
import 'react-quill-new/dist/quill.snow.css';

// Dynamically import ReactQuill to avoid SSR issues
const ReactQuill = lazy(() => import('react-quill-new'));

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { 
  FileText, BookOpen, Link as LinkIcon, Save, Plus, Edit, Trash2, 
  GripVertical, Eye, EyeOff, ExternalLink, AlertCircle, Video, FolderCog
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Sortable row wrapper ──────────────────────────────────────────────────────
const SortableRow = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 50 : 'auto' }}
      className="flex items-stretch gap-1"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="flex items-center px-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
        title="Drag to reorder"
        onClick={(e) => e.stopPropagation()}
        data-testid={`row-drag-handle-${id}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

// Quill editor configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    ['link'],
    ['clean']
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent', 'link'
];

const PageBuilder = () => {
  const [activeTab, setActiveTab] = useState('legal');
  
  // Legal pages state
  const [legalPages, setLegalPages] = useState({
    privacy_policy: '',
    terms_of_use: '',
    refund_policy: ''
  });
  const [selectedLegalPage, setSelectedLegalPage] = useState('privacy_policy');
  const [savingLegal, setSavingLegal] = useState(false);
  
  // User guide state
  const [userGuideSections, setUserGuideSections] = useState([]);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm, setSectionForm] = useState({ title: '', content: '', video_embeds: [], category_id: '' });
  const [savingSection, setSavingSection] = useState(false);
  
  // Guidelines state
  const [guidelineArticles, setGuidelineArticles] = useState([]);
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({ title: '', summary: '', content: '', is_published: true, category_id: '', grades: [], video_embeds: [] });
  const [savingArticle, setSavingArticle] = useState(false);

  // Category management state — one "Manage categories" modal is reused
  // for both Docs (user_guide) and Guides (guidelines) via `catSurface`.
  // `catForm` holds inline add/edit-in-place values.
  const [docCategories, setDocCategories] = useState([]);
  const [guideCategories, setGuideCategories] = useState([]);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [catSurface, setCatSurface] = useState('docs'); // 'docs' | 'guides'
  const [catNew, setCatNew] = useState('');
  const [catEditingId, setCatEditingId] = useState(null);
  const [catEditName, setCatEditName] = useState('');
  const [savingCat, setSavingCat] = useState(false);

  // DnD sensors
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // External links state
  const [externalLinks, setExternalLinks] = useState({ contact_us_url: '', bulk_submit_url: '', written_submit_url: '' });
  const [savingLinks, setSavingLinks] = useState(false);

  // Community (Dayroom) settings state — grade-aware community links +
  // footer link + dismissible dashboard banner. See routes/community.py
  // for the smart-redirect endpoint that consumes `grade_urls`.
  // Note: MEO Class 3 doesn't exist as a grade in this platform — the
  // engineering ladder goes MEO Class 1 → MEO Class 2 → MEO Class 4.
  const SUPPORTED_GRADES = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG'];
  const [communitySettings, setCommunitySettings] = useState({
    grade_urls: {},
    footer_enabled: true,
    footer_label: 'Join Community',
    banner_enabled: false,
    banner_title: 'Connect with your peers',
    banner_body: "Join your grade's Dayroom community to chat, ask doubts and share notes.",
    banner_cta_label: 'Join now',
    banner_delay_days: 5,
  });
  const [savingCommunity, setSavingCommunity] = useState(false);

  // Fetch all data on mount
  useEffect(() => {
    fetchLegalPages();
    fetchUserGuideSections();
    fetchGuidelineArticles();
    fetchExternalLinks();
    fetchCommunitySettings();
    fetchDocCategories();
    fetchGuideCategories();
  }, []);

  // ==================== LEGAL PAGES ====================
  const fetchLegalPages = async () => {
    try {
      const response = await axios.get(`${API}/pages/legal`);
      setLegalPages({
        privacy_policy: response.data.privacy_policy || '',
        terms_of_use: response.data.terms_of_use || '',
        refund_policy: response.data.refund_policy || ''
      });
    } catch (error) {
      console.error('Error fetching legal pages:', error);
    }
  };

  const saveLegalPage = async () => {
    try {
      setSavingLegal(true);
      await axios.put(`${API}/admin/pages/legal`, {
        page_type: selectedLegalPage,
        content: legalPages[selectedLegalPage]
      });
      toast.success('Legal page saved successfully');
    } catch (error) {
      toast.error('Failed to save legal page');
    } finally {
      setSavingLegal(false);
    }
  };

  // ==================== USER GUIDE ====================
  // ── Drag-and-drop reorder handlers ───────────────────────────────────────
  const handleSectionDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setUserGuideSections((prev) => {
      const oldIdx = prev.findIndex((s) => s.id === active.id);
      const newIdx = prev.findIndex((s) => s.id === over.id);
      const reordered = arrayMove(prev, oldIdx, newIdx);
      axios.post(`${API}/admin/pages/user-guide/reorder`, {
        section_ids: reordered.map((s) => s.id),
      }).catch(() => toast.error('Failed to save section order'));
      return reordered;
    });
  }, []);

  const handleArticleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setGuidelineArticles((prev) => {
      const oldIdx = prev.findIndex((a) => a.id === active.id);
      const newIdx = prev.findIndex((a) => a.id === over.id);
      const reordered = arrayMove(prev, oldIdx, newIdx);
      axios.post(`${API}/admin/pages/guidelines/reorder`, {
        article_ids: reordered.map((a) => a.id),
      }).catch(() => toast.error('Failed to save article order'));
      return reordered;
    });
  }, []);

  const handleCategoryDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const setter = catSurface === 'docs' ? setDocCategories : setGuideCategories;
    setter((prev) => {
      const oldIdx = prev.findIndex((c) => c.id === active.id);
      const newIdx = prev.findIndex((c) => c.id === over.id);
      const reordered = arrayMove(prev, oldIdx, newIdx);
      axios.post(`${API}/admin/pages/${catSurface}-categories/reorder`, {
        category_ids: reordered.map((c) => c.id),
      }).catch(() => toast.error('Failed to save category order'));
      return reordered;
    });
  }, [catSurface]);

  // ─────────────────────────────────────────────────────────────────────────
  const fetchUserGuideSections = async () => {
    try {
      const response = await axios.get(`${API}/admin/pages/user-guide`);
      setUserGuideSections(response.data);
    } catch (error) {
      console.error('Error fetching user guide sections:', error);
    }
  };

  const openSectionDialog = (section = null) => {
    if (section) {
      setEditingSection(section);
      setSectionForm({
        title: section.title,
        content: section.content,
        video_embeds: Array.isArray(section.video_embeds) ? section.video_embeds : [],
        category_id: section.category_id || '',
      });
    } else {
      setEditingSection(null);
      setSectionForm({ title: '', content: '', video_embeds: [], category_id: '' });
    }
    setSectionDialogOpen(true);
  };

  const saveSection = async () => {
    if (!sectionForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    try {
      setSavingSection(true);
      if (editingSection) {
        await axios.put(`${API}/admin/pages/user-guide/${editingSection.id}`, sectionForm);
        toast.success('Section updated successfully');
      } else {
        await axios.post(`${API}/admin/pages/user-guide`, sectionForm);
        toast.success('Section created successfully');
      }
      setSectionDialogOpen(false);
      fetchUserGuideSections();
    } catch (error) {
      // Backend rejects unsupported providers or empty video URLs with
      // a clean 400. Surface that message directly so admins know which
      // video row to fix.
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save section');
    } finally {
      setSavingSection(false);
    }
  };

  const toggleSectionActive = async (section) => {
    try {
      await axios.put(`${API}/admin/pages/user-guide/${section.id}`, {
        is_active: !section.is_active
      });
      toast.success(section.is_active ? 'Section hidden' : 'Section visible');
      fetchUserGuideSections();
    } catch (error) {
      toast.error('Failed to update section');
    }
  };

  const deleteSection = async (section) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;
    
    try {
      await axios.delete(`${API}/admin/pages/user-guide/${section.id}`);
      toast.success('Section deleted successfully');
      fetchUserGuideSections();
    } catch (error) {
      toast.error('Failed to delete section');
    }
  };

  // ==================== GUIDELINES ====================
  const fetchGuidelineArticles = async () => {
    try {
      const response = await axios.get(`${API}/admin/pages/guidelines`);
      setGuidelineArticles(response.data);
    } catch (error) {
      console.error('Error fetching guideline articles:', error);
    }
  };

  const openArticleDialog = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setArticleForm({
        title: article.title,
        summary: article.summary || '',
        content: article.content,
        is_published: article.is_published,
        category_id: article.category_id || '',
        grades: Array.isArray(article.grades) ? article.grades : [],
        video_embeds: Array.isArray(article.video_embeds) ? article.video_embeds : [],
      });
    } else {
      setEditingArticle(null);
      setArticleForm({ title: '', summary: '', content: '', is_published: true, category_id: '', grades: [], video_embeds: [] });
    }
    setArticleDialogOpen(true);
  };

  const saveArticle = async () => {
    if (!articleForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    try {
      setSavingArticle(true);
      if (editingArticle) {
        await axios.put(`${API}/admin/pages/guidelines/${editingArticle.id}`, articleForm);
        toast.success('Article updated successfully');
      } else {
        await axios.post(`${API}/admin/pages/guidelines`, articleForm);
        toast.success('Article created successfully');
      }
      setArticleDialogOpen(false);
      fetchGuidelineArticles();
    } catch (error) {
      // Backend rejects unsupported video providers or empty URLs with
      // a clean 400. Surface that message so admins know which row to fix.
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save article');
    } finally {
      setSavingArticle(false);
    }
  };

  const toggleArticlePublished = async (article) => {
    try {
      await axios.put(`${API}/admin/pages/guidelines/${article.id}`, {
        is_published: !article.is_published
      });
      toast.success(article.is_published ? 'Article unpublished' : 'Article published');
      fetchGuidelineArticles();
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  const deleteArticle = async (article) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      await axios.delete(`${API}/admin/pages/guidelines/${article.id}`);
      toast.success('Article deleted successfully');
      fetchGuidelineArticles();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  // ==================== EXTERNAL LINKS ====================
  const fetchExternalLinks = async () => {
    try {
      const response = await axios.get(`${API}/pages/external-links`);
      setExternalLinks({
        contact_us_url: response.data.contact_us_url || '',
        bulk_submit_url: response.data.bulk_submit_url || '',
        written_submit_url: response.data.written_submit_url || ''
      });
    } catch (error) {
      console.error('Error fetching external links:', error);
    }
  };

  const saveExternalLinks = async () => {
    try {
      setSavingLinks(true);
      await axios.put(`${API}/admin/pages/external-links`, externalLinks);
      toast.success('External links saved successfully');
    } catch (error) {
      toast.error('Failed to save external links');
    } finally {
      setSavingLinks(false);
    }
  };

  // ===== Category management (Docs + Guides) =====
  // Two separate collections, one modal, `catSurface` picks which list.
  const fetchDocCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/pages/docs-categories`);
      setDocCategories(data);
    } catch (e) {
      console.error('Failed to load doc categories:', e);
    }
  };
  const fetchGuideCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/pages/guides-categories`);
      setGuideCategories(data);
    } catch (e) {
      console.error('Failed to load guide categories:', e);
    }
  };

  const openCategoryDialog = (surface) => {
    setCatSurface(surface);
    setCatNew('');
    setCatEditingId(null);
    setCatEditName('');
    setCatDialogOpen(true);
  };

  const refreshCategoriesForSurface = () => {
    if (catSurface === 'docs') fetchDocCategories(); else fetchGuideCategories();
  };

  const addCategory = async () => {
    const name = catNew.trim();
    if (!name) return;
    try {
      setSavingCat(true);
      await axios.post(`${API}/admin/pages/${catSurface}-categories`, { name });
      setCatNew('');
      toast.success('Category added');
      refreshCategoriesForSurface();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to add category');
    } finally {
      setSavingCat(false);
    }
  };

  const saveCategoryEdit = async (categoryId) => {
    const name = catEditName.trim();
    if (!name) return;
    try {
      setSavingCat(true);
      await axios.put(`${API}/admin/pages/${catSurface}-categories/${categoryId}`, { name });
      setCatEditingId(null);
      setCatEditName('');
      toast.success('Category updated');
      refreshCategoriesForSurface();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update category');
    } finally {
      setSavingCat(false);
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/pages/${catSurface}-categories/${cat.id}`);
      toast.success('Category deleted');
      refreshCategoriesForSurface();
    } catch (e) {
      // Soft-block on in-use categories surfaces here.
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete category');
    }
  };

  // Nautical grade options — matches SUPPORTED_GRADES for the Guidelines
  // multi-select. Empty list ⇒ visible to all grades (product decision).
  const GRADE_OPTIONS = SUPPORTED_GRADES;

  // ===== Community (Dayroom) =====
  const fetchCommunitySettings = async () => {
    try {
      const { data } = await axios.get(`${API}/pages/community-settings`);
      setCommunitySettings(prev => ({
        ...prev,
        ...data,
        grade_urls: data.grade_urls || {},
      }));
    } catch (e) {
      console.error('Failed to load community settings:', e);
    }
  };

  const saveCommunitySettings = async () => {
    try {
      setSavingCommunity(true);
      // Strip empty URLs so we don't persist blank rows the smart-redirect
      // would treat as a real "join" target.
      const cleanedGradeUrls = Object.fromEntries(
        Object.entries(communitySettings.grade_urls || {})
          .filter(([, v]) => v && v.trim())
          .map(([k, v]) => [k, v.trim()])
      );
      await axios.put(`${API}/admin/pages/community-settings`, {
        ...communitySettings,
        grade_urls: cleanedGradeUrls,
        banner_delay_days: Number(communitySettings.banner_delay_days) || 0,
      });
      toast.success('Community settings saved');
    } catch (e) {
      toast.error('Failed to save community settings');
    } finally {
      setSavingCommunity(false);
    }
  };

  const legalPageLabels = {
    privacy_policy: 'Privacy Policy',
    terms_of_use: 'Terms of Use',
    refund_policy: 'Refund Policy'
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#0A7EA4]" />
            Page Builder
          </CardTitle>
          <CardDescription>
            Manage your website&apos;s legal pages, user guide, guidelines, and external links.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="legal">Legal Pages</TabsTrigger>
              <TabsTrigger value="guide">User Guide</TabsTrigger>
              <TabsTrigger value="guidelines">Guidelines</TabsTrigger>
              <TabsTrigger value="links">External Links</TabsTrigger>
              <TabsTrigger value="community" data-testid="page-builder-tab-community">Community</TabsTrigger>
            </TabsList>

            {/* Legal Pages Tab */}
            <TabsContent value="legal" className="space-y-4">
              <div className="flex gap-2 mb-4">
                {Object.keys(legalPageLabels).map((key) => (
                  <Button
                    key={key}
                    variant={selectedLegalPage === key ? 'default' : 'outline'}
                    onClick={() => setSelectedLegalPage(key)}
                    size="sm"
                  >
                    {legalPageLabels[key]}
                  </Button>
                ))}
              </div>
              
              <div className="border rounded-lg">
                <Suspense fallback={
                  <div className="min-h-[400px] bg-gray-50 border rounded-lg">
                    <div className="border-b bg-white p-2 h-10"></div>
                    <div className="flex items-center justify-center h-[360px] text-gray-400">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A7EA4] mx-auto mb-2"></div>
                        <span>Loading editor...</span>
                      </div>
                    </div>
                  </div>
                }>
                  <ReactQuill
                    theme="snow"
                    value={legalPages[selectedLegalPage]}
                    onChange={(value) => setLegalPages({ ...legalPages, [selectedLegalPage]: value })}
                    modules={quillModules}
                    formats={quillFormats}
                    className="min-h-[400px]"
                  />
                </Suspense>
              </div>
              
              <div className="flex justify-between items-center pt-4">
                <a 
                  href={`/${selectedLegalPage.replace('_', '-')}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-[#0A7EA4] hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> Preview page
                </a>
                <Button onClick={saveLegalPage} disabled={savingLegal}>
                  <Save className="w-4 h-4 mr-2" />
                  {savingLegal ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </TabsContent>

            {/* User Guide Tab */}
            <TabsContent value="guide" className="space-y-4">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <div>
                  <p className="text-sm text-gray-600">
                    Create sections for your user guide. Users will see them as expandable accordion items.
                  </p>
                  <p className="text-xs italic text-gray-500 mt-1" data-testid="page-builder-docs-label">
                    Displayed to users as <span className="font-semibold not-italic">Docs</span>.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => openCategoryDialog('docs')} data-testid="manage-doc-categories-btn">
                    <FolderCog className="w-4 h-4 mr-2" /> Manage Categories
                  </Button>
                  <Button onClick={() => openSectionDialog()}>
                    <Plus className="w-4 h-4 mr-2" /> Add Section
                  </Button>
                </div>
              </div>

              {userGuideSections.length > 0 ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
                  <SortableContext items={userGuideSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {userGuideSections.map((section, index) => {
                        const cat = docCategories.find(c => c.id === section.category_id);
                        return (
                          <SortableRow key={section.id} id={section.id}>
                            <div
                              className={`flex items-center justify-between p-3 border rounded-lg ${!section.is_active ? 'bg-gray-50 opacity-60' : ''}`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <span className="text-sm font-medium text-gray-500 w-6">{index + 1}.</span>
                                <span className={`truncate ${section.is_active ? '' : 'line-through'}`}>{section.title}</span>
                                {cat && <Badge variant="outline" className="text-xs">{cat.name}</Badge>}
                                {!section.is_active && <Badge variant="secondary">Hidden</Badge>}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={() => toggleSectionActive(section)} title={section.is_active ? 'Hide section' : 'Show section'}>
                                  {section.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openSectionDialog(section)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteSection(section)}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </SortableRow>
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No sections yet. Create your first user guide section.</p>
                </div>
              )}
              
              <div className="pt-4">
                <a 
                  href="/help/docs" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-[#0A7EA4] hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> Preview Docs
                </a>
              </div>
            </TabsContent>

            {/* Guidelines Tab */}
            <TabsContent value="guidelines" className="space-y-4">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <div>
                  <p className="text-sm text-gray-600">
                    Create guideline articles to share best practices and expert tips.
                  </p>
                  <p className="text-xs italic text-gray-500 mt-1" data-testid="page-builder-guides-label">
                    Displayed to users as <span className="font-semibold not-italic">Guides</span>.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => openCategoryDialog('guides')} data-testid="manage-guide-categories-btn">
                    <FolderCog className="w-4 h-4 mr-2" /> Manage Categories
                  </Button>
                  <Button onClick={() => openArticleDialog()}>
                    <Plus className="w-4 h-4 mr-2" /> Add Article
                  </Button>
                </div>
              </div>

              {guidelineArticles.length > 0 ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleArticleDragEnd}>
                  <SortableContext items={guidelineArticles.map(a => a.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2">
                      {guidelineArticles.map((article) => {
                        const cat = guideCategories.find(c => c.id === article.category_id);
                        const grades = Array.isArray(article.grades) ? article.grades : [];
                        return (
                          <SortableRow key={article.id} id={article.id}>
                            <div
                              className={`flex items-center justify-between p-3 border rounded-lg ${!article.is_published ? 'bg-gray-50 opacity-60' : ''}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={article.is_published ? 'font-medium' : 'font-medium line-through'}>
                                    {article.title}
                                  </span>
                                  {cat && <Badge variant="outline" className="text-xs">{cat.name}</Badge>}
                                  {grades.length === 0 ? (
                                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">All grades</Badge>
                                  ) : (
                                    grades.map(g => (
                                      <Badge key={g} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">{g}</Badge>
                                    ))
                                  )}
                                  {!article.is_published && <Badge variant="secondary">Draft</Badge>}
                                </div>
                                {article.summary && (
                                  <p className="text-sm text-gray-500 mt-1 line-clamp-1">{article.summary}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button variant="ghost" size="sm" onClick={() => toggleArticlePublished(article)} title={article.is_published ? 'Unpublish' : 'Publish'}>
                                  {article.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => openArticleDialog(article)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteArticle(article)}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </SortableRow>
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No articles yet. Create your first guideline article.</p>
                </div>
              )}
              
              <div className="pt-4">
                <a 
                  href="/help/guides" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-[#0A7EA4] hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> Preview Guides
                </a>
              </div>
            </TabsContent>

            {/* External Links Tab */}
            <TabsContent value="links" className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">External Form Links</p>
                    <p className="mt-1">These URLs will be used for the Contact Us page and the Bulk Submit button on the Submit Questions page.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="contact_url">Contact Us Form URL</Label>
                  <Input
                    id="contact_url"
                    type="url"
                    placeholder="https://notion.so/your-contact-form"
                    value={externalLinks.contact_us_url}
                    onChange={(e) => setExternalLinks({ ...externalLinks, contact_us_url: e.target.value })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This link will be shown on the Contact Us page.
                  </p>
                </div>

                <div>
                  <Label htmlFor="bulk_url">Bulk Submit Form URL</Label>
                  <Input
                    id="bulk_url"
                    type="url"
                    placeholder="https://notion.so/your-bulk-submit-form"
                    value={externalLinks.bulk_submit_url}
                    onChange={(e) => setExternalLinks({ ...externalLinks, bulk_submit_url: e.target.value })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This link will be shown as a button on the Submit Questions page.
                  </p>
                </div>

                <div>
                  <Label htmlFor="written_url">Written Questions Submit URL</Label>
                  <Input
                    id="written_url"
                    type="url"
                    placeholder="https://notion.so/your-written-submit-form"
                    value={externalLinks.written_submit_url}
                    onChange={(e) => setExternalLinks({ ...externalLinks, written_submit_url: e.target.value })}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Notion form link for users to submit written exam papers (PDF/JPG). Shown on Submit Questions page.
                  </p>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={saveExternalLinks} disabled={savingLinks}>
                  <Save className="w-4 h-4 mr-2" />
                  {savingLinks ? 'Saving...' : 'Save Links'}
                </Button>
              </div>
            </TabsContent>

            {/* ===== Community (Dayroom) tab ===== */}
            <TabsContent value="community" className="space-y-6" data-testid="page-builder-community-tab">
              <div className="rounded-md border border-blue-100 bg-blue-50/50 p-3 text-sm text-blue-900">
                One smart redirect (<code className="bg-white px-1 rounded text-xs">/api/community/go</code>) reads each user&apos;s current grade and sends them to the matching Dayroom URL. Footer link + dashboard banner both use this — no per-surface URLs to maintain.
              </div>

              {/* Grade → URL mappings */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Dayroom URLs per grade</Label>
                <p className="text-xs text-gray-500">Leave a field blank to suppress the community CTA for that grade.</p>
                <div className="space-y-2 mt-2">
                  {SUPPORTED_GRADES.map(grade => (
                    <div key={grade} className="flex items-center gap-3">
                      <div className="w-36 text-sm font-medium text-gray-700">{grade}</div>
                      <Input
                        type="url"
                        value={communitySettings.grade_urls?.[grade] || ''}
                        placeholder="https://dayroom.app/…"
                        onChange={(e) =>
                          setCommunitySettings({
                            ...communitySettings,
                            grade_urls: { ...communitySettings.grade_urls, [grade]: e.target.value },
                          })
                        }
                        className="flex-1"
                        data-testid={`community-url-input-${grade.replace(/\s+/g, '-')}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer link */}
              <div className="space-y-2 border-t pt-4">
                <Label className="text-base font-semibold">Footer link</Label>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Show &quot;Join Community&quot; in the footer</p>
                    <p className="text-xs text-gray-500">Always points to the smart redirect — resolves per user&apos;s grade.</p>
                  </div>
                  <Switch
                    checked={communitySettings.footer_enabled}
                    onCheckedChange={(v) => setCommunitySettings({ ...communitySettings, footer_enabled: v })}
                    data-testid="community-footer-enabled-switch"
                  />
                </div>
                <div>
                  <Label className="text-xs">Footer label</Label>
                  <Input
                    value={communitySettings.footer_label}
                    onChange={(e) => setCommunitySettings({ ...communitySettings, footer_label: e.target.value })}
                    placeholder="Join Community"
                    data-testid="community-footer-label-input"
                  />
                </div>
              </div>

              {/* Dismissible banner */}
              <div className="space-y-3 border-t pt-4">
                <Label className="text-base font-semibold">Dashboard banner</Label>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Show on dashboard</p>
                    <p className="text-xs text-gray-500">Dismissible per-user. Earned discovery — appears only after N days.</p>
                  </div>
                  <Switch
                    checked={communitySettings.banner_enabled}
                    onCheckedChange={(v) => setCommunitySettings({ ...communitySettings, banner_enabled: v })}
                    data-testid="community-banner-enabled-switch"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Title</Label>
                    <Input
                      value={communitySettings.banner_title}
                      onChange={(e) => setCommunitySettings({ ...communitySettings, banner_title: e.target.value })}
                      data-testid="community-banner-title-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">CTA label</Label>
                    <Input
                      value={communitySettings.banner_cta_label}
                      onChange={(e) => setCommunitySettings({ ...communitySettings, banner_cta_label: e.target.value })}
                      data-testid="community-banner-cta-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Body</Label>
                  <Textarea
                    rows={2}
                    value={communitySettings.banner_body}
                    onChange={(e) => setCommunitySettings({ ...communitySettings, banner_body: e.target.value })}
                    data-testid="community-banner-body-input"
                  />
                </div>
                <div className="max-w-xs">
                  <Label className="text-xs">Show after (days)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={communitySettings.banner_delay_days}
                    onChange={(e) => setCommunitySettings({ ...communitySettings, banner_delay_days: e.target.value })}
                    data-testid="community-banner-delay-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">User&apos;s account must be at least this many days old before the banner shows.</p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button
                  onClick={saveCommunitySettings}
                  disabled={savingCommunity}
                  data-testid="community-save-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingCommunity ? 'Saving…' : 'Save community settings'}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Section Dialog */}
      <Dialog open={sectionDialogOpen} onOpenChange={setSectionDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSection ? 'Edit Section' : 'Add New Section'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="section-title">Section Title *</Label>
              <Input
                id="section-title"
                value={sectionForm.title}
                onChange={(e) => setSectionForm({ ...sectionForm, title: e.target.value })}
                placeholder="e.g., Getting Started"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="section-category">Category</Label>
              <Select
                value={sectionForm.category_id || 'none'}
                onValueChange={(v) => setSectionForm({ ...sectionForm, category_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger id="section-category" className="mt-1" data-testid="section-category-select">
                  <SelectValue placeholder="Uncategorised" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Uncategorised</SelectItem>
                  {docCategories.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Manage categories from the top of the User Guide tab.
              </p>
            </div>
            
            <div>
              <Label>Content</Label>
              <div className="border rounded-lg mt-1">
                <Suspense fallback={
                  <div className="min-h-[300px] bg-gray-50 rounded-lg">
                    <div className="border-b bg-white p-2 h-10"></div>
                    <div className="flex items-center justify-center h-[260px] text-gray-400">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0A7EA4] mx-auto mb-2"></div>
                        <span className="text-sm">Loading editor...</span>
                      </div>
                    </div>
                  </div>
                }>
                  <ReactQuill
                    theme="snow"
                    value={sectionForm.content}
                    onChange={(value) => setSectionForm({ ...sectionForm, content: value })}
                    modules={quillModules}
                    formats={quillFormats}
                    className="min-h-[300px]"
                  />
                </Suspense>
              </div>
            </div>

            {/* ── Video embeds ────────────────────────────────────────
                Mirrors the Notes pattern. Admin pastes a bare URL, a
                share link, or a full iframe HTML for YouTube or Dadan (dadan.io / video.helmtorq.com);
                the backend normalises to a canonical /embed/... URL on
                save. On save error (unsupported provider / empty URL),
                the 400 message from backend surfaces via toast. */}
            <div data-testid="user-guide-video-embeds">
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2">
                  <Video className="w-4 h-4 text-[#0A7EA4]" />
                  Videos (optional)
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSectionForm(f => ({
                    ...f,
                    video_embeds: [...(f.video_embeds || []), { url: '', title: '' }],
                  }))}
                  data-testid="user-guide-add-video-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add video
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Paste a YouTube or Dadan link, share URL, or embed HTML — we&apos;ll normalise it.
              </p>
              {(sectionForm.video_embeds || []).length === 0 ? (
                <p className="text-xs text-gray-400 italic">No videos added.</p>
              ) : (
                <div className="space-y-2">
                  {(sectionForm.video_embeds || []).map((v, idx) => (
                    <div
                      key={idx}
                      className="border rounded-md p-2 bg-gray-50 flex flex-col sm:flex-row gap-2"
                      data-testid={`user-guide-video-row-${idx}`}
                    >
                      <Input
                        type="text"
                        value={v.title || ''}
                        placeholder="Title (optional)"
                        className="sm:w-48"
                        onChange={(e) => setSectionForm(f => {
                          const arr = [...(f.video_embeds || [])];
                          arr[idx] = { ...arr[idx], title: e.target.value };
                          return { ...f, video_embeds: arr };
                        })}
                        data-testid={`user-guide-video-title-${idx}`}
                      />
                      <Input
                        type="text"
                        value={v.url || ''}
                        placeholder="YouTube or Dadan URL / embed HTML (dadan.io or video.helmtorq.com)"
                        className="flex-1 font-mono text-xs"
                        onChange={(e) => setSectionForm(f => {
                          const arr = [...(f.video_embeds || [])];
                          arr[idx] = { ...arr[idx], url: e.target.value };
                          return { ...f, video_embeds: arr };
                        })}
                        data-testid={`user-guide-video-url-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setSectionForm(f => ({
                          ...f,
                          video_embeds: (f.video_embeds || []).filter((_, i) => i !== idx),
                        }))}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`user-guide-video-remove-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSectionDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveSection} disabled={savingSection}>
              {savingSection ? 'Saving...' : (editingSection ? 'Update Section' : 'Create Section')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Article Dialog */}
      <Dialog open={articleDialogOpen} onOpenChange={setArticleDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingArticle ? 'Edit Article' : 'Add New Article'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="article-title">Article Title *</Label>
              <Input
                id="article-title"
                value={articleForm.title}
                onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                placeholder="e.g., Best Practices for Exam Preparation"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="article-category">Category</Label>
              <Select
                value={articleForm.category_id || 'none'}
                onValueChange={(v) => setArticleForm({ ...articleForm, category_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger id="article-category" className="mt-1" data-testid="article-category-select">
                  <SelectValue placeholder="Uncategorised" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Uncategorised</SelectItem>
                  {guideCategories.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Manage categories from the top of the Guidelines tab.
              </p>
            </div>

            {/* Grade filter — empty list ⇒ visible to all grades.
                Checkboxes rather than a combobox because these are
                already a small, exhaustive list and admins get instant
                visual confirmation of scope. */}
            <div data-testid="article-grades-picker">
              <Label>Grades</Label>
              <p className="text-xs text-gray-500 mb-2">
                Leave all unchecked to show this article to <span className="font-medium">every grade</span>.
                Tick specific grades to restrict visibility.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 border rounded-md p-3 bg-gray-50/50">
                {GRADE_OPTIONS.map(g => {
                  const checked = articleForm.grades.includes(g);
                  return (
                    <label
                      key={g}
                      className="flex items-center gap-2 text-sm cursor-pointer select-none"
                      data-testid={`grade-checkbox-${g.replace(/\s+/g, '-')}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => setArticleForm(f => ({
                          ...f,
                          grades: v
                            ? [...f.grades, g]
                            : f.grades.filter(x => x !== g),
                        }))}
                      />
                      <span>{g}</span>
                    </label>
                  );
                })}
              </div>
              {articleForm.grades.length === 0 && (
                <p className="text-xs mt-2 text-green-700">
                  <span className="font-medium">Visible to all grades.</span>
                </p>
              )}
            </div>
            
            <div>
              <Label htmlFor="article-summary">Summary (optional)</Label>
              <Textarea
                id="article-summary"
                value={articleForm.summary}
                onChange={(e) => setArticleForm({ ...articleForm, summary: e.target.value })}
                placeholder="A brief summary that appears in the article list..."
                className="mt-1"
                rows={2}
              />
            </div>
            
            <div>
              <Label>Content</Label>
              <div className="border rounded-lg mt-1">
                <Suspense fallback={
                  <div className="min-h-[300px] bg-gray-50 rounded-lg">
                    <div className="border-b bg-white p-2 h-10"></div>
                    <div className="flex items-center justify-center h-[260px] text-gray-400">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0A7EA4] mx-auto mb-2"></div>
                        <span className="text-sm">Loading editor...</span>
                      </div>
                    </div>
                  </div>
                }>
                  <ReactQuill
                    theme="snow"
                    value={articleForm.content}
                    onChange={(value) => setArticleForm({ ...articleForm, content: value })}
                    modules={quillModules}
                    formats={quillFormats}
                    className="min-h-[300px]"
                  />
                </Suspense>
              </div>
            </div>

            {/* Video embeds — mirrors the User Guide section-dialog UI.
                Admin pastes YouTube/Dadan URL (dadan.io / video.helmtorq.com), share link, or full
                iframe HTML; backend normalises to canonical /embed/…
                on save. On save error the 400 message surfaces via
                the article-save toast. */}
            <div data-testid="article-video-embeds">
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2">
                  <Video className="w-4 h-4 text-[#0A7EA4]" />
                  Videos (optional)
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setArticleForm(f => ({
                    ...f,
                    video_embeds: [...(f.video_embeds || []), { url: '', title: '' }],
                  }))}
                  data-testid="article-add-video-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add video
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Paste a YouTube or Dadan link, share URL, or embed HTML &mdash; we&apos;ll normalise it.
              </p>
              {(articleForm.video_embeds || []).length === 0 ? (
                <p className="text-xs text-gray-400 italic">No videos added.</p>
              ) : (
                <div className="space-y-2">
                  {(articleForm.video_embeds || []).map((v, idx) => (
                    <div
                      key={idx}
                      className="border rounded-md p-2 bg-gray-50 flex flex-col sm:flex-row gap-2"
                      data-testid={`article-video-row-${idx}`}
                    >
                      <Input
                        type="text"
                        value={v.title || ''}
                        placeholder="Title (optional)"
                        className="sm:w-48"
                        onChange={(e) => setArticleForm(f => {
                          const arr = [...(f.video_embeds || [])];
                          arr[idx] = { ...arr[idx], title: e.target.value };
                          return { ...f, video_embeds: arr };
                        })}
                        data-testid={`article-video-title-${idx}`}
                      />
                      <Input
                        type="text"
                        value={v.url || ''}
                        placeholder="YouTube or Dadan URL / embed HTML (dadan.io or video.helmtorq.com)"
                        className="flex-1 font-mono text-xs"
                        onChange={(e) => setArticleForm(f => {
                          const arr = [...(f.video_embeds || [])];
                          arr[idx] = { ...arr[idx], url: e.target.value };
                          return { ...f, video_embeds: arr };
                        })}
                        data-testid={`article-video-url-${idx}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setArticleForm(f => ({
                          ...f,
                          video_embeds: (f.video_embeds || []).filter((_, i) => i !== idx),
                        }))}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`article-video-remove-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="article-published"
                checked={articleForm.is_published}
                onCheckedChange={(checked) => setArticleForm({ ...articleForm, is_published: checked })}
              />
              <Label htmlFor="article-published">Publish immediately</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setArticleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveArticle} disabled={savingArticle}>
              {savingArticle ? 'Saving...' : (editingArticle ? 'Update Article' : 'Create Article')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Categories Dialog — reused for Docs + Guides */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="manage-categories-dialog">
          <DialogHeader>
            <DialogTitle>
              Manage {catSurface === 'docs' ? 'Docs' : 'Guides'} Categories
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={catNew}
                onChange={(e) => setCatNew(e.target.value)}
                placeholder="New category name"
                onKeyDown={(e) => { if (e.key === 'Enter') addCategory(); }}
                data-testid="new-category-input"
              />
              <Button onClick={addCategory} disabled={savingCat || !catNew.trim()} data-testid="add-category-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>

            {(() => {
              const list = catSurface === 'docs' ? docCategories : guideCategories;
              if (list.length === 0) {
                return (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No categories yet. Add one above.
                  </p>
                );
              }
              return (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCategoryDragEnd}>
                  <SortableContext items={list.map(c => c.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1 max-h-80 overflow-y-auto">
                      {list.map((c) => (
                        <SortableRow key={c.id} id={c.id}>
                          <div
                            className="flex items-center justify-between p-2 border rounded-md"
                            data-testid={`category-row-${c.id}`}
                          >
                            {catEditingId === c.id ? (
                              <div className="flex flex-1 gap-2">
                                <Input
                                  value={catEditName}
                                  onChange={(e) => setCatEditName(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') saveCategoryEdit(c.id); }}
                                  autoFocus
                                />
                                <Button size="sm" onClick={() => saveCategoryEdit(c.id)} disabled={savingCat}>Save</Button>
                                <Button size="sm" variant="ghost" onClick={() => { setCatEditingId(null); setCatEditName(''); }}>Cancel</Button>
                              </div>
                            ) : (
                              <>
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-sm">{c.name}</span>
                                  {typeof c.article_count === 'number' && c.article_count > 0 && (
                                    <Badge variant="secondary" className="text-xs">
                                      {c.article_count} in use
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setCatEditingId(c.id); setCatEditName(c.name); }}
                                    data-testid={`edit-category-${c.id}`}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteCategory(c)}
                                    data-testid={`delete-category-${c.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        </SortableRow>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PageBuilder;
